import React from "react";
import {
	Button,
	ClassNameProps,
	Conditional,
	Else,
	Heading,
	If,
	Modal,
	Row,
	StyleProps,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { SlimEmptyView } from "../../../../../Components/Empty";
import { PaginatedGrid } from "../../../../../Components/PaginatedGrid";
import { Tile } from "../../../../../Components/Tile";
import { HomeRoomSectionLink } from "../../../../Links";
import { HomeRoomSectionCardProps, HomeRoomSectionSimpleCard } from "../../Card";
import { HomeRoomSectionsListModal } from "../../ListModal";

export type HomeRoomSectionsPaginatedGridTileProps = {
	isLoading?: boolean;
	homeRoomSections: HomeRoomSectionCardProps["homeRoomSection"][] | null | undefined;
	columns: number;
	rows?: number;
	slim?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function HomeRoomSectionsPaginatedGridTile({
	isLoading = false,
	homeRoomSections,
	columns,
	rows = 1,
	slim,
	className,
	style,
}: HomeRoomSectionsPaginatedGridTileProps) {
	const numHomeRoomSections = homeRoomSections?.length ?? 0;

	const pageSize = columns * rows;

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Tile className={className} style={style}>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Heading.H2 noMargin>{toGrammaticalNumber("Home Room", numHomeRoomSections)}</Heading.H2>

						<If condition={numHomeRoomSections > pageSize}>
							<Button variant="link" size="small" onClick={openModal}>
								View All
							</Button>
						</If>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<Conditional>
						<If condition={isLoading && !homeRoomSections}>Loading...</If>
						<If condition={!isLoading && numHomeRoomSections === 0}>
							<SlimEmptyView>No home rooms listed</SlimEmptyView>
						</If>
						<Else>
							<PaginatedGrid columns={columns} rows={rows} items={homeRoomSections} gap="0.75rem">
								{(homeRoomSection) => (
									<HomeRoomSectionLink
										key={homeRoomSection.id}
										instance={homeRoomSection}
										className="link--no-text-decoration"
									>
										<HomeRoomSectionSimpleCard homeRoomSection={homeRoomSection} slim={slim} />
									</HomeRoomSectionLink>
								)}
							</PaginatedGrid>
						</Else>
					</Conditional>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<HomeRoomSectionsListModal isLoading={isLoading} homeRoomSections={homeRoomSections} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
